import { CheckCircle2, XCircle } from "lucide-react";

/**
 * Brief 3.5 PR 2 §1.3 — Status stepper.
 *
 * Linear stepper across the four "happy path" states. Special states
 * collapse as follows (per Dioni signoff):
 *   • info_requested → renders as a sub-label on the "Under review"
 *     step ("⏸ Awaiting merchant docs") so the operator sees that
 *     the merchant is in-flight but the ball is in the merchant's court.
 *   • declined / cancelled / terminated → the stepper is replaced
 *     entirely with a single red "Closed: {reason}" pill.
 *
 * Pure presentational. Reads `identity.status` only.
 */
const STEPS = [
  { key: "submitted",    label: "Submitted" },
  { key: "under_review", label: "Under review" },
  { key: "approved",     label: "Approved" },
  { key: "live",         label: "Live" },
];

const CLOSED_LABELS = {
  declined:   "Closed — declined",
  cancelled:  "Closed — cancelled",
  terminated: "Closed — terminated",
};

// Which step are we "on" given the lifecycle status? Returns the index
// in STEPS, or -1 if the merchant hasn't reached the first step yet
// (prospect / draft / awaiting_documents).
function _stepIndex(status) {
  if (status === "live") return 3;
  if (status === "approved") return 2;
  if (status === "under_review" || status === "info_requested") return 1;
  if (status === "submitted") return 0;
  return -1;
}

export default function StatusStepper({ identity }) {
  const status = identity?.status;

  // Closed terminal — replace the entire stepper.
  if (status && CLOSED_LABELS[status]) {
    return (
      <div
        data-testid="merchant-status-stepper"
        data-status={status}
        className="inline-flex items-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/[0.08] px-3.5 py-1.5"
      >
        <XCircle className="h-3.5 w-3.5 text-rose-200" />
        <span className="text-[12.5px] text-rose-100" data-testid="merchant-status-closed-label">
          {CLOSED_LABELS[status]}
        </span>
      </div>
    );
  }

  // Pre-submission — show stepper with no current step highlighted.
  const current = _stepIndex(status);
  const isInfoRequested = status === "info_requested";

  return (
    <div
      data-testid="merchant-status-stepper"
      data-status={status || "pre_submission"}
      className="flex items-center gap-1.5 flex-wrap"
    >
      {STEPS.map((s, i) => {
        const reached = current >= i;
        const isCurrent = current === i;
        return (
          <div key={s.key} className="flex items-center gap-1.5">
            <div
              data-testid={`merchant-status-step-${s.key}`}
              data-state={isCurrent ? "current" : reached ? "complete" : "future"}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] transition-colors ${
                isCurrent
                  ? "border border-cyan-400/40 bg-cyan-400/[0.10] text-cyan-100"
                  : reached
                    ? "border border-emerald-400/30 bg-emerald-500/[0.05] text-emerald-100/90"
                    : "border border-white/[0.08] bg-white/[0.02] text-zinc-500"
              }`}
            >
              {reached && !isCurrent && <CheckCircle2 className="h-3 w-3" />}
              {s.label}
            </div>
            {/* Sub-label on Under review when ball is in merchant's court. */}
            {s.key === "under_review" && isCurrent && isInfoRequested && (
              <span
                data-testid="merchant-status-step-under_review-substate"
                className="text-[11.5px] text-amber-200/90"
              >
                ⏸ Awaiting merchant docs
              </span>
            )}
            {i < STEPS.length - 1 && (
              <span className={`mx-0.5 text-[11px] ${reached ? "text-emerald-400/60" : "text-zinc-700"}`}>→</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
